import { useState } from 'react';
import { DollarSign, Eye, MousePointer, Target, BarChart3, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MetricCard } from './MetricCard';
import { FeedbackState } from './FeedbackState';
import { CountryDetailModal } from './CountryDetailModal';
import { DomainDetailModal } from './DomainDetailModal';
import { fmt } from '../utils/helpers';
import {GeographicAnalysis} from "@/pages/Dashboard/components/GeographicAnalysis.tsx";
import {ChartTooltip} from "@/pages/Dashboard";
import {DomainPerformance} from "@/pages/Dashboard/components/DomainPerformance.tsx";

interface AdUnitViewProps {
    data: any;
    isLoading: boolean;
    isError: boolean;
    error: any;
}

export function AdUnitView({ data, isLoading, isError, error }: AdUnitViewProps) {
    const [activeMetrics, setActiveMetrics] = useState<string[]>(['revenue', 'impressions']);
    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const [countryDetailModal, setCountryDetailModal] = useState<any>(null);
    const [domainDetailModal, setDomainDetailModal] = useState<any>(null);
    const [chartType, setChartType] = useState<'line' | 'area'>('area');

    const handleMetricToggle = (metric: string) => {
        if (activeMetrics.includes(metric)) {
            if (activeMetrics.length > 1) {
                setActiveMetrics(activeMetrics.filter(m => m !== metric));
            }
        } else {
            setActiveMetrics([...activeMetrics, metric]);
        }
    };

    if (isLoading) {
        return (
            <div className="rounded-xl bg-white p-16 shadow-sm border border-gray-200">
                <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-blue-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }} />
                    </div>
                    <p className="text-gray-600 font-medium">Carregando dados...</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <FeedbackState
                type="error"
                title="Erro ao Carregar Dados"
                message={error?.message || 'Não foi possível carregar os dados de receita. Tente novamente.'}
            />
        );
    }

    if (!data) {
        return (
            <FeedbackState
                type="empty"
                title="Nenhum Dado Disponível"
                message="Não há dados para o período selecionado. Ajuste os filtros e tente novamente."
            />
        );
    }

    const metricOptions = [
        { key: 'revenue', label: 'Receita', color: '#3b82f6', icon: DollarSign },
        { key: 'impressions', label: 'Impressões', color: '#10b981', icon: Eye },
        { key: 'clicks', label: 'Cliques', color: '#8b5cf6', icon: MousePointer },
        { key: 'ecpm', label: 'eCPM', color: '#f59e0b', icon: Target },
        { key: 'active_view', label: 'Viewability', color: '#06b6d4', icon: Activity },
    ];

    return (
        <div className="space-y-6">
            {/* Métricas Principais */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
                <MetricCard
                    label="Receita Total"
                    value={fmt.currency(data.summary?.total_revenue || 0)}
                    icon={DollarSign}
                    theme="primary"
                />
                <MetricCard
                    label="Impressões"
                    value={fmt.number(data.summary?.total_impressions || 0)}
                    icon={Eye}
                    theme="success"
                    details={[
                        { key: 'Não preenchidas', val: fmt.number(data.summary?.unfilled_impressions || 0) }
                    ]}
                />
                <MetricCard
                    label="Cliques"
                    value={fmt.number(data.summary?.total_clicks || 0)}
                    icon={MousePointer}
                    theme="purple"
                    details={[
                        { key: 'CTR', val: fmt.percent(data.summary?.avg_ctr || 0) }
                    ]}
                />
                <MetricCard
                    label="eCPM Médio"
                    value={fmt.currency(data.summary?.avg_ecpm || 0)}
                    icon={Target}
                    theme="warning"
                />
                <MetricCard
                    label="Viewability"
                    value={fmt.percent(data.summary?.avg_active_view || 0)}
                    icon={Activity}
                    theme="info"
                    details={[
                        { key: 'Viewable', val: fmt.number(data.summary?.viewable_impressions || 0) },
                        { key: 'Measurable', val: fmt.number(data.summary?.measurable_impressions || 0) }
                    ]}
                />
            </div>

            {/* Gráfico de Evolução */}
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-blue-600" />
                            Evolução Temporal
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">Análise diária de métricas</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {/* Tipo de gráfico */}
                        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                            <button
                                onClick={() => setChartType('area')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    chartType === 'area' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Área
                            </button>
                            <button
                                onClick={() => setChartType('line')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    chartType === 'line' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                                }`}
                            >
                                Linha
                            </button>
                        </div>

                        {/* Seletores de métrica */}
                        {metricOptions.map((metric) => {
                            const Icon = metric.icon;
                            return (
                                <button
                                    key={metric.key}
                                    onClick={() => handleMetricToggle(metric.key)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                        activeMetrics.includes(metric.key)
                                            ? 'bg-white text-gray-900 shadow-md border-2'
                                            : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                                    }`}
                                    style={activeMetrics.includes(metric.key) ? { borderColor: metric.color } : {}}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {metric.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {data.charts?.by_date?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={380}>
                        {chartType === 'area' ? (
                            <AreaChart data={data.charts.by_date}>
                                <defs>
                                    {metricOptions.map(metric => (
                                        <linearGradient key={metric.key} id={`gradient-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={metric.color} stopOpacity={0.3} />
                                            <stop offset="95%" stopColor={metric.color} stopOpacity={0} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12, fill: '#6b7280' }}
                                    tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: ptBR })}
                                />
                                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend />
                                {activeMetrics.includes('revenue') && (
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        name="Receita"
                                        stroke="#3b82f6"
                                        strokeWidth={2.5}
                                        fill="url(#gradient-revenue)"
                                    />
                                )}
                                {activeMetrics.includes('impressions') && (
                                    <Area
                                        type="monotone"
                                        dataKey="impressions"
                                        name="Impressões"
                                        stroke="#10b981"
                                        strokeWidth={2.5}
                                        fill="url(#gradient-impressions)"
                                    />
                                )}
                                {activeMetrics.includes('clicks') && (
                                    <Area
                                        type="monotone"
                                        dataKey="clicks"
                                        name="Cliques"
                                        stroke="#8b5cf6"
                                        strokeWidth={2.5}
                                        fill="url(#gradient-clicks)"
                                    />
                                )}
                                {activeMetrics.includes('ecpm') && (
                                    <Area
                                        type="monotone"
                                        dataKey="ecpm"
                                        name="eCPM"
                                        stroke="#f59e0b"
                                        strokeWidth={2.5}
                                        fill="url(#gradient-ecpm)"
                                    />
                                )}
                                {activeMetrics.includes('active_view') && (
                                    <Area
                                        type="monotone"
                                        dataKey="active_view"
                                        name="Viewability"
                                        stroke="#06b6d4"
                                        strokeWidth={2.5}
                                        fill="url(#gradient-active_view)"
                                    />
                                )}
                            </AreaChart>
                        ) : (
                            <LineChart data={data.charts.by_date}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 12, fill: '#6b7280' }}
                                    tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: ptBR })}
                                />
                                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                                <Tooltip content={<ChartTooltip />} />
                                <Legend />
                                {activeMetrics.includes('revenue') && (
                                    <Line
                                        type="monotone"
                                        dataKey="revenue"
                                        name="Receita"
                                        stroke="#3b82f6"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#3b82f6' }}
                                        activeDot={{ r: 6 }}
                                    />
                                )}
                                {activeMetrics.includes('impressions') && (
                                    <Line
                                        type="monotone"
                                        dataKey="impressions"
                                        name="Impressões"
                                        stroke="#10b981"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#10b981' }}
                                        activeDot={{ r: 6 }}
                                    />
                                )}
                                {activeMetrics.includes('clicks') && (
                                    <Line
                                        type="monotone"
                                        dataKey="clicks"
                                        name="Cliques"
                                        stroke="#8b5cf6"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#8b5cf6' }}
                                        activeDot={{ r: 6 }}
                                    />
                                )}
                                {activeMetrics.includes('ecpm') && (
                                    <Line
                                        type="monotone"
                                        dataKey="ecpm"
                                        name="eCPM"
                                        stroke="#f59e0b"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#f59e0b' }}
                                        activeDot={{ r: 6 }}
                                    />
                                )}
                                {activeMetrics.includes('active_view') && (
                                    <Line
                                        type="monotone"
                                        dataKey="active_view"
                                        name="Viewability"
                                        stroke="#06b6d4"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#06b6d4' }}
                                        activeDot={{ r: 6 }}
                                    />
                                )}
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                ) : (
                    <div className="h-[380px] flex items-center justify-center">
                        <FeedbackState
                            type="empty"
                            title="Sem Dados de Evolução"
                            message="Não há dados temporais disponíveis para exibir"
                        />
                    </div>
                )}
            </div>

            <GeographicAnalysis
                data={data}
                selectedCountry={selectedCountry}
                setSelectedCountry={setSelectedCountry}
                setCountryDetailModal={setCountryDetailModal}
            />


            <DomainPerformance
                data={data}
                setDomainDetailModal={setDomainDetailModal}
            />


            {/* Modais */}
            {countryDetailModal && (
                <CountryDetailModal
                    country={countryDetailModal}
                    onClose={() => setCountryDetailModal(null)}
                />
            )}

            {domainDetailModal && (
                <DomainDetailModal
                    domain={domainDetailModal}
                    onClose={() => setDomainDetailModal(null)}
                />
            )}
        </div>
    );
}